import type { IdeaCardLayout } from '@/lib/idea-card-types';

export type MarkdownDensity = 'compact' | 'relaxed';

export interface IdeaCardPresentation {
  density: MarkdownDensity;
  cardContentClassName: string;
  markdownClassName: string;
}

const SECTION_LABEL_RE = /[:\uFF1A]\s*$/;
const LIST_ITEM_RE = /^\s*(?:[-*+]|\d+\.)\s+/;
const BLOCK_LINE_RE = /^\s*(?:[-*+]\s+|\d+\.\s+|>\s*|```|~~~|\|)/;

const isSectionLabelLine = (line: string): boolean => {
  const trimmed = line.trim();
  if (!trimmed || !SECTION_LABEL_RE.test(trimmed)) return false;
  if (LIST_ITEM_RE.test(trimmed) || trimmed.startsWith('|')) return false;

  const bareLabel = trimmed
    .replace(/[*_`~]/g, '')
    .replace(SECTION_LABEL_RE, '')
    .trim();

  return bareLabel.length > 0 && bareLabel.length <= 48;
};

const DEFAULT_PRESENTATION: IdeaCardPresentation = {
  density: 'compact',
  cardContentClassName: 'leading-tight',
  markdownClassName: 'prose-ul:my-0 prose-ol:my-0 prose-li:my-0 prose-p:my-0',
};

const PRESENTATION_BY_LAYOUT: Record<Exclude<IdeaCardLayout, undefined>, IdeaCardPresentation> = {
  title: DEFAULT_PRESENTATION,
  vertical: DEFAULT_PRESENTATION,
  horizontal: {
    density: 'relaxed',
    cardContentClassName: 'leading-relaxed',
    markdownClassName: 'prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-1',
  },
};

export const getIdeaCardPresentation = (layout?: IdeaCardLayout): IdeaCardPresentation =>
  layout ? PRESENTATION_BY_LAYOUT[layout] : DEFAULT_PRESENTATION;

export const formatIdeaCardBodyForMarkdown = (body?: string): string => {
  if (!body) return '';

  const lines = body.split(/\r?\n/);
  if (lines.length <= 1) return body;

  let formatted = lines[0] ?? '';

  for (let index = 1; index < lines.length; index += 1) {
    const previousLine = lines[index - 1] ?? '';
    const currentLine = lines[index] ?? '';
    const previousTrimmed = previousLine.trim();
    const currentTrimmed = currentLine.trim();

    if (!previousTrimmed || !currentTrimmed) {
      formatted += `\n${currentLine}`;
      continue;
    }

    if (isSectionLabelLine(previousLine)) {
      formatted += `  \n${currentLine}`;
      continue;
    }

    if (BLOCK_LINE_RE.test(previousTrimmed) || BLOCK_LINE_RE.test(currentTrimmed)) {
      formatted += `\n${currentLine}`;
      continue;
    }

    formatted += `\n\n${currentLine}`;
  }

  return formatted;
};
